import { useEffect, useState } from "react";

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Find the first `[data-tour="<targetId>"]` element the user can actually see,
 * with its viewport rect. An anchor counts as missing when it is:
 *  - not in the DOM (e.g. the mobile sidebar Sheet is closed, so its items are
 *    unmounted);
 *  - zero-size (`display: none` on it or an ancestor, or a collapsed node);
 *  - `visibility: hidden`;
 *  - horizontally off-screen (an off-canvas drawer translated out of view —
 *    vertical overflow is fine, the caller scrolls it into view).
 * When several nodes share the id (e.g. a desktop + a mobile copy), the first
 * visible one wins.
 */
export function findVisibleTarget(
  targetId: string,
): { el: HTMLElement; rect: Rect } | null {
  const nodes = document.querySelectorAll<HTMLElement>(
    `[data-tour="${CSS.escape(targetId)}"]`,
  );
  const vw = window.innerWidth;
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    if (r.right <= 0 || r.left >= vw) continue;
    if (window.getComputedStyle(el).visibility === "hidden") continue;
    return { el, rect: { top: r.top, left: r.left, width: r.width, height: r.height } };
  }
  return null;
}

/**
 * Track the viewport rect of the visible `[data-tour="<targetId>"]` element so
 * the spotlight + tooltip follow it through scroll, resize, and layout shifts.
 * Returns `null` while the target is absent or hidden (see
 * {@link findVisibleTarget}) — the caller renders a centered card in that case.
 *
 * On first resolve it scrolls the target into view (centered) so the highlighted
 * element is never off-screen.
 */
export function useTargetRect(targetId: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!targetId) {
      setRect(null);
      return;
    }

    let raf = 0;
    let scrolledIntoView = false;

    const measure = () => {
      const found = findVisibleTarget(targetId);
      if (!found) {
        setRect(null);
        return;
      }
      if (!scrolledIntoView) {
        scrolledIntoView = true;
        found.el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
      setRect(found.rect);
    };

    // Poll briefly via rAF so the rect tracks animated/late-mounting targets
    // without a heavy MutationObserver; cheap because the overlay is short-lived.
    let running = true;
    const loop = () => {
      if (!running) return;
      measure();
      raf = requestAnimationFrame(loop);
    };
    loop();

    window.addEventListener("resize", measure);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [targetId]);

  return rect;
}
